import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClassrooms } from "../data/ClassroomContext";
import { useLearnerDetail } from "../data/useClassroomWorkspace";
import { BackIcon } from "../components/Icons";
import { WorkSteps } from "../components/LearnerWork";
import Observations from "../components/Observations";
import { Message } from "../components/Status";
import WorkspaceStatus from "../components/WorkspaceStatus";
import {
  ReviewSummary,
  StatePill,
  StepProgress,
  formatWhen,
  lessonText,
} from "../components/WorkState";
import ui from "../components/ui.module.css";

const whenText = (item) =>
  item.completedAt
    ? `Finished ${formatWhen(item.completedAt)}`
    : item.startedAt
    ? `Started ${formatWhen(item.startedAt)}`
    : null;

// Completed work still waiting is reviewed in Work; reviewed work can be reopened.
const ReviewAction = ({ item, learnerId }) => {
  if (item.state !== "completed" && item.state !== "reviewed") return null;
  const needsReview = item.state === "completed";
  return (
    <Link
      to={`/work/${item.attemptId}`}
      state={{ from: `/learners/${learnerId}` }}
      className={`${ui.button} ${needsReview ? ui.primary : ui.secondary}`}
    >
      {needsReview ? "Review work" : "Open review"}
    </Link>
  );
};

const WorkHeading = ({ item, as: Heading = "h3" }) => (
  <div className={ui.stack} style={{ gap: "0.25rem" }}>
    <span className={ui.learnerCardTop}>
      <Heading className={ui.learnerName}>{item.activity.title}</Heading>
      <StatePill state={item.state} />
    </span>
    <p className={ui.meta}>
      {[lessonText(item), whenText(item)].filter(Boolean).join(" · ")}
    </p>
    <StepProgress completedSteps={item.completedSteps} totalSteps={item.totalSteps} />
  </div>
);

const WorkBody = ({ item, learner }) => (
  <>
    {item.state === "completed" && (
      <p className={`${ui.notice} ${ui.warning}`}>This work still needs your review.</p>
    )}
    <ReviewSummary review={item.review} />
    <WorkSteps work={item.work || []} learnerName={learner.name} />
    <ReviewAction item={item} learnerId={learner.id} />
  </>
);

// Earlier work opens on demand, so its saved pictures load only when wanted.
const EarlierWork = ({ item, learner }) => {
  const [open, setOpen] = useState(false);
  return (
    <details className={ui.details} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>
        <WorkHeading item={item} as="span" />
      </summary>
      {open && (
        <div className={ui.stack}>
          <WorkBody item={item} learner={learner} />
        </div>
      )}
    </details>
  );
};

const LearnerDetail = () => {
  const { learnerId } = useParams();
  const { active } = useClassrooms();
  const detail = useLearnerDetail(active.id, learnerId);
  const data = detail.data;

  const back = (
    <Link to="/learners" className={ui.textButton}>
      <BackIcon />
      Learners
    </Link>
  );

  if (!data) {
    return (
      <div className={ui.page}>
        {back}
        <WorkspaceStatus
          resource={detail}
          loadingLabel="Loading this learner…"
          className={active.name}
          notFound={
            <Message
              title={`This learner isn't in ${active.name}`}
              actions={
                <Link to="/learners" className={`${ui.button} ${ui.primary}`}>
                  See your learners
                </Link>
              }
            >
              They may have moved class, or this link is for another class.
            </Message>
          }
        />
      </div>
    );
  }

  const { learner, currentWork, recentWork = [], observations = [] } = data;
  const earlier = recentWork.filter((item) => item.attemptId !== currentWork?.attemptId);
  const byAttempt = new Map(recentWork.map((item) => [item.attemptId, item.activity.title]));

  return (
    <div className={ui.page}>
      {back}
      <div className={ui.stack}>
        <h1 className={ui.title}>{learner.name}</h1>
        <p className={ui.eyebrow}>{active.name}</p>
      </div>

      <WorkspaceStatus resource={detail} className={active.name} />

      <section className={ui.stack} aria-labelledby="current-work">
        <h2 id="current-work" className={ui.cardTitle}>
          Latest work
        </h2>
        {currentWork ? (
          <div className={`${ui.card} ${ui.stack}`}>
            <WorkHeading item={currentWork} />
            <WorkBody item={currentWork} learner={learner} />
          </div>
        ) : (
          <p className={ui.meta}>{learner.name} hasn't started any work yet.</p>
        )}
      </section>

      <Observations
        classroomId={active.id}
        learnerId={learner.id}
        observations={observations}
        work={recentWork
          .filter((item) => item.attemptId)
          .map((item) => ({
            attemptId: item.attemptId,
            deliveryId: item.deliveryId,
            title: item.activity.title,
            when: whenText(item),
          }))}
        describe={(item) => (item.attemptId && byAttempt.get(item.attemptId)) || null}
      />

      {earlier.length > 0 && (
        <section className={ui.stack} aria-labelledby="earlier-work">
          <h2 id="earlier-work" className={ui.cardTitle}>
            Earlier work
          </h2>
          <ul className={ui.list}>
            {earlier.map((item) => (
              <li key={item.attemptId}>
                <EarlierWork item={item} learner={learner} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default LearnerDetail;
